import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Locate, MapPin, Navigation, Crosshair } from 'lucide-react';
import { toast } from 'sonner';

// Fix default icon paths for Leaflet bundled with React
const DefaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const LiveIcon = L.divIcon({
  className: 'memoir-live-icon',
  html: `<span style="display:inline-block;height:14px;width:14px;border-radius:9999px;background:hsl(173 80% 36%);box-shadow:0 0 0 6px hsla(173,80%,36%,0.25);border:2px solid white;"></span>`,
  iconSize: [14, 14], iconAnchor: [7, 7],
});

function Recenter({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) { map.setView(points[0], 12); return; }
    map.fitBounds(points, { padding: [40, 40] });
  }, [points, map]);
  return null;
}

export default function MapTab({ event }) {
  const [stops, setStops] = useState([]);
  const [live, setLive] = useState([]);
  const [mediaPins, setMediaPins] = useState([]);
  const [showMediaPins, setShowMediaPins] = useState(true);
  const [sharing, setSharing] = useState(false);

  const load = async () => {
    try {
      const [iR, lR, mR] = await Promise.all([
        api.get(`/events/${event.id}/itinerary`),
        api.get(`/events/${event.id}/location/latest`),
        api.get(`/events/${event.id}/media?has_gps=true&limit=200`),
      ]);
      const all = [];
      (iR.data || []).forEach((d) => (d.stops || []).forEach((s) => { if (s.lat && s.lng) all.push({ ...s, day: d.day_number || d.title }); }));
      setStops(all);
      setLive(lR.data || []);
      setMediaPins((mR.data || []).filter((m) => m.exif_lat != null && m.exif_lng != null));
    } catch (e) {}
  };
  useEffect(() => { load(); }, [event.id]);

  const stopPoints = useMemo(() => stops.map((s) => [s.lat, s.lng]), [stops]);
  const livePoints = useMemo(() => live.map((l) => [l.lat, l.lng]), [live]);
  const mediaPoints = useMemo(() => mediaPins.map((m) => [m.exif_lat, m.exif_lng]), [mediaPins]);
  const allPoints = useMemo(() => [...stopPoints, ...livePoints, ...(showMediaPins ? mediaPoints : [])], [stopPoints, livePoints, mediaPoints, showMediaPins]);
  const center = allPoints[0] || [37.7749, -122.4194]; // SF default

  const shareLocation = () => {
    if (!navigator.geolocation) return toast.error('Geolocation not supported');
    setSharing(true);
    const watcher = navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        await api.post(`/events/${event.id}/location`, { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        toast.success('Location shared');
        load();
      } catch (err) {
        console.error('Failed to share location:', err);
        toast.error('Failed to share location');
      } finally {
        setSharing(false);
      }
    }, (err) => {
      console.error('Geolocation error:', err);
      toast.error(err.message || 'Could not get location');
      setSharing(false);
    }, { enableHighAccuracy: true, timeout: 10000 });
    return watcher;
  };

  return (
    <div className="space-y-3" data-testid="map-tab">
      <Card className="rounded-2xl overflow-hidden">
        <div className="h-72 sm:h-96 relative">
          <MapContainer center={center} zoom={5} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false} className="rounded-2xl" data-testid="map-container">
            <TileLayer attribution='© OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Recenter points={allPoints} />
            {stopPoints.length >= 2 && <Polyline positions={stopPoints} pathOptions={{ color: '#0d9488', weight: 4, opacity: 0.85 }} />}
            {stops.map((s) => (
              <Marker key={s.id || `stop-${s.lat}-${s.lng}`} position={[s.lat, s.lng]}>
                <Popup>
                  <div className="text-sm font-semibold">{s.title}</div>
                  {s.location_text && <div className="text-xs text-muted-foreground">{s.location_text}</div>}
                  {s.day && <div className="text-[10px] text-muted-foreground">Day {s.day}</div>}
                </Popup>
              </Marker>
            ))}
            {live.map((l) => (
              <Marker key={l.user_id || `live-${l.lat}-${l.lng}`} position={[l.lat, l.lng]} icon={LiveIcon}>
                <Popup><div className="text-sm font-semibold">{l.user?.name || 'Member'}</div></Popup>
              </Marker>
            ))}
            {showMediaPins && mediaPins.map((m, i) => (
              <Marker key={`media-${m.id || i}`} position={[m.exif_lat, m.exif_lng]}>
                <Popup>
                  <div className="w-40">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/media/${m.id}/thumb`} alt="" className="w-full h-24 object-cover rounded" />
                    {m.caption && <div className="text-xs mt-1 line-clamp-2">{m.caption}</div>}
                    <div className="text-[10px] text-muted-foreground mt-0.5">{m.taken_at ? new Date(m.taken_at).toLocaleString() : ''}</div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </Card>
      <div className="flex gap-2 flex-wrap items-center">
        <Button onClick={shareLocation} disabled={sharing} data-testid="share-location-button" className="rounded-xl gradient-fab text-white"><Locate className="h-4 w-4 mr-2" />{sharing ? 'Locating…' : 'Share my location'}</Button>
        <Button onClick={load} variant="outline" data-testid="map-refresh" className="rounded-xl"><Crosshair className="h-4 w-4 mr-2" />Refresh</Button>
        <label className="ml-auto flex items-center gap-2 text-xs text-muted-foreground" data-testid="media-pins-toggle-label">
          <input type="checkbox" checked={showMediaPins} onChange={(e) => setShowMediaPins(e.target.checked)} data-testid="media-pins-toggle" />
          Show photo pins ({mediaPins.length})
        </label>
      </div>
      {stops.length === 0 && (
        <Card className="rounded-2xl p-4 text-sm text-muted-foreground">Add stops with coordinates in the Itinerary tab to plot a route.</Card>
      )}
    </div>
  );
}
